import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Card } from "react-bootstrap";
import "./MyPerformanceDashboardPage.css";
import api from "../../api";
import DashboardFilterBar from "../../components/dashboard/DashboardFilterBar";
import DataQualityNotice from "../../components/dashboard/DataQualityNotice";
import WorkloadSummary from "../../components/dashboard/WorkloadSummary";
import TatComplianceSummary from "../../components/dashboard/TatComplianceSummary";
import FormPerformanceSummary from "../../components/dashboard/FormPerformanceSummary";
import UatSummary from "../../components/dashboard/UatSummary";
import MetricCard from "../../components/dashboard/MetricCard";
import PerformanceScoreBreakdown from "../../components/dashboard/PerformanceScoreBreakdown";

// Phase 5, §30. "My Dashboard" — own data only, always available (no
// permission grant required beyond authentication). Composite score
// section only renders when a FINALIZED/CALCULATED scorecard exists for
// the caller in the selected period — otherwise raw metrics stand alone.
export default function MyPerformanceDashboardPage() {
  const [filters, setFilters] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scorecard, setScorecard] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        project_id: filters.project_id || undefined,
        department_id: filters.department_id || undefined,
      };
      const res = await api.get("/dashboards/my-summary", { params });
      setSummary(res.data?.data || null);
    } catch (err) {
      console.error("Fetch my-summary error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    // Dedicated self-scoped endpoint — always the caller's own latest
    // scorecard, never derived from a list call that could return another
    // employee's most-recently-calculated scorecard.
    api.get("/performance-scorecards/my-latest")
      .then((res) => setScorecard(res.data?.data || null))
      .catch(() => setScorecard(null));
  }, []);

  const cards = summary?.summaryCards || {};

  return (
    <div className="unit-page dashboard-shell mpd-page">
      <div className="unit-header mpd-heading-box">
        <div className="mpd-heading-content">
          <div className="mpd-heading-icon"><i className="fa-solid fa-gauge-high" aria-hidden="true"></i></div>
          <div>
            <div className="unit-page-label">Performance</div>
            <h2>My Dashboard</h2>
            <p>Your own task accountability, contribution, forms, UAT and approvals — no ranking, no comparison to others.</p>
          </div>
        </div>
      </div>

      <DashboardFilterBar filters={filters} onChange={setFilters} showDepartment={false} />

      {loading ? (
        <div className="perf-skeleton-row">
          {[0, 1, 2, 3].map((i) => <div key={i} className="perf-skeleton-card" />)}
        </div>
      ) : (
        <>
          <DataQualityNotice warnings={summary?.warnings} />

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-list-check" aria-hidden="true"></i>Assigned Task Accountability</h6>
            <WorkloadSummary accountability={cards.taskAccountability} />
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-clock" aria-hidden="true"></i>TAT</h6>
            <TatComplianceSummary accountability={cards.taskAccountability} />
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-chart-line" aria-hidden="true"></i>Work Performed (Contribution)</h6>
            <Row className="g-3">
              <Col xs={6} md={3}><MetricCard label="Tasks Performed" value={cards.taskContribution?.tasksPerformed} direction="INFORMATIONAL_ONLY" icon="fa-list" /></Col>
              <Col xs={6} md={3}><MetricCard label="Performer Completed" value={cards.taskContribution?.performerCompletedCount} direction="HIGHER_IS_BETTER" icon="fa-check-double" /></Col>
              <Col xs={6} md={3}><MetricCard label="Work Logs Recorded" value={cards.taskContribution?.workLogCount} direction="INFORMATIONAL_ONLY" icon="fa-file-lines" /></Col>
              <Col xs={6} md={3}><MetricCard label="Effort Minutes" value={cards.taskContribution?.effortMinutes} direction="INFORMATIONAL_ONLY" icon="fa-hourglass-half" /></Col>
            </Row>
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-file-lines" aria-hidden="true"></i>Assigned Forms &amp; Results</h6>
            <FormPerformanceSummary formSubmission={cards.forms} />
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-vial" aria-hidden="true"></i>UAT</h6>
            <UatSummary uat={cards.uat} />
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-circle-check" aria-hidden="true"></i>Approvals</h6>
            <Row className="g-3">
              <Col xs={6} md={3}><MetricCard label="Requests Raised" value={cards.approvalsRaised?.requestsRaised} direction="INFORMATIONAL_ONLY" icon="fa-paper-plane" /></Col>
              <Col xs={6} md={3}><MetricCard label="Requests Pending" value={cards.approvalsRaised?.pendingCount} direction="INFORMATIONAL_ONLY" icon="fa-hourglass-half" /></Col>
              <Col xs={6} md={3}><MetricCard label="Approvals Decided" value={cards.approvalsDecided?.approvalsDecided} direction="INFORMATIONAL_ONLY" icon="fa-circle-check" /></Col>
              <Col xs={6} md={3}><MetricCard label="Avg Turnaround" value={cards.approvalsRaised?.avgTurnaroundHours} unit="hours" direction="LOWER_IS_BETTER" icon="fa-stopwatch" /></Col>
            </Row>
          </Card.Body></Card>

          {scorecard && (
            <Card className="perf-section-card"><Card.Body>
              <h6 className="perf-section-title"><i className="fa-solid fa-trophy" aria-hidden="true"></i>Composite Score — {scorecard.period_name} ({scorecard.profile_name})</h6>
              <PerformanceScoreBreakdown scorecard={scorecard} />
            </Card.Body></Card>
          )}
        </>
      )}
    </div>
  );
}
